import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { BoardStage, CreateDealInput, CreatePipelineInput, CreateStageInput, DealItem, Paginated, PipelineItem, StageItem, UpdateDealInput } from '@crm/shared';
import { api } from '../../api/client';

export interface DealParams {
  pipelineId?: string;
  stageId?: string;
  contactId?: string;
  organizationId?: string;
  agentId?: string;
  status?: 'open' | 'won' | 'lost' | 'all';
  q?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface Board {
  pipeline: { id: string; name: string; isDefault: boolean };
  stages: BoardStage[];
}

export const usePipelines = (enabled = true) => useQuery({ queryKey: ['pipelines'], queryFn: async () => (await api.get<PipelineItem[]>('/pipelines')).data, enabled });

export function useDeals(params: DealParams, enabled = true) {
  return useQuery({
    queryKey: ['deals', params],
    queryFn: async () => (await api.get<Paginated<DealItem> & { totalValue: number }>('/deals', { params })).data,
    placeholderData: keepPreviousData,
    enabled,
  });
}
export function useBoard(pipelineId: string | undefined, filters: { q?: string; agentId?: string } = {}) {
  return useQuery({
    queryKey: ['board', pipelineId, filters],
    queryFn: async () => (await api.get<Board>('/deals/board', { params: { pipelineId, ...filters } })).data,
    enabled: !!pipelineId,
    placeholderData: keepPreviousData,
  });
}
export function useDeal(id: string | undefined) {
  return useQuery({ queryKey: ['deal', id], queryFn: async () => (await api.get<DealItem & { counts: { openTasks: number } }>(`/deals/${id}`)).data, enabled: !!id });
}

function useInvalidate(keys: string[][]) {
  const qc = useQueryClient();
  return () => keys.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
}
const DEAL_KEYS = [['deals'], ['board'], ['deal'], ['activities'], ['dashboard'], ['contact'], ['organization'], ['pipelines']];

export function useCreateDeal() {
  const inv = useInvalidate(DEAL_KEYS);
  return useMutation({ mutationFn: async (input: CreateDealInput) => (await api.post<DealItem>('/deals', input)).data, onSuccess: inv });
}
export function useUpdateDeal() {
  const inv = useInvalidate(DEAL_KEYS);
  return useMutation({ mutationFn: async ({ id, ...input }: UpdateDealInput & { id: string }) => (await api.patch<DealItem>(`/deals/${id}`, input)).data, onSuccess: inv });
}
export function useMoveDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: string; stageId: string; sortIndex?: number; lostReason?: string | null }) => (await api.patch<DealItem>(`/deals/${id}/move`, input)).data,
    onSettled: () => DEAL_KEYS.forEach((k) => void qc.invalidateQueries({ queryKey: k })),
  });
}
export function useDeleteDeals() {
  const inv = useInvalidate(DEAL_KEYS);
  return useMutation({ mutationFn: async (ids: string[]) => (await api.post<{ deleted: number }>('/deals/bulk-delete', { ids })).data, onSuccess: inv });
}

// --- pipelines admin ------------------------------------------------------------

export function useCreatePipeline() {
  const inv = useInvalidate([['pipelines']]);
  return useMutation({ mutationFn: async (input: CreatePipelineInput) => (await api.post<PipelineItem>('/pipelines', input)).data, onSuccess: inv });
}
export function useUpdatePipeline() {
  const inv = useInvalidate([['pipelines'], ['board']]);
  return useMutation({ mutationFn: async ({ id, ...input }: { id: string; name?: string; isDefault?: boolean }) => (await api.patch<PipelineItem>(`/pipelines/${id}`, input)).data, onSuccess: inv });
}
export function useDeletePipeline() {
  const inv = useInvalidate([['pipelines']]);
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/pipelines/${id}`)).data, onSuccess: inv });
}
export function useAddStage() {
  const inv = useInvalidate([['pipelines'], ['board']]);
  return useMutation({ mutationFn: async ({ pipelineId, ...input }: CreateStageInput & { pipelineId: string }) => (await api.post<StageItem>(`/pipelines/${pipelineId}/stages`, input)).data, onSuccess: inv });
}
export function useUpdateStage() {
  const inv = useInvalidate([['pipelines'], ['board'], ['deals']]);
  return useMutation({ mutationFn: async ({ id, ...input }: Partial<CreateStageInput> & { id: string }) => (await api.patch<StageItem>(`/pipelines/stages/${id}`, input)).data, onSuccess: inv });
}
export function useReorderStages() {
  const inv = useInvalidate([['pipelines'], ['board']]);
  return useMutation({ mutationFn: async ({ pipelineId, ids }: { pipelineId: string; ids: string[] }) => (await api.post<PipelineItem>(`/pipelines/${pipelineId}/stages/reorder`, { ids })).data, onSuccess: inv });
}
export function useDeleteStage() {
  const inv = useInvalidate([['pipelines'], ['board'], ['deals']]);
  return useMutation({ mutationFn: async ({ id, moveDealsTo }: { id: string; moveDealsTo?: string }) => (await api.delete<PipelineItem>(`/pipelines/stages/${id}`, { params: { moveDealsTo } })).data, onSuccess: inv });
}

export const dealContactName = (c: { firstName: string | null; lastName: string | null; email: string | null } | null) =>
  c ? [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || '' : '';

export const money = (value: number | null, currency: string) =>
  value === null ? '—' : new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);

/** Probability-weighted value of a stage; stages without a probability count as 0. */
export const weightedValue = (stage: Pick<BoardStage, 'totalValue' | 'probability'>) =>
  Math.round((stage.totalValue * (stage.probability ?? 0)) / 100);
